import React, {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {Chatbot} from '../../states/actions';
import {useSelector, shallowEqual} from 'react-redux';
import {IntNodeComponent, TextareaComponent, SelectComp, InputComponent} from './Components';
import {createSelectNodeData} from '../../states/selectors/chatbotSelectors';
import {axios} from '../../libs/axios';
import {
  estimateClassifierCost,
  formatCostUsd,
  formatContextWindow,
  groupCatalogueByProvider,
  estimateTokens,
} from '../Match/classifierCostPure';
import {
  getClassifierModelCapabilities,
  defaultAnthropicLlmOptionsForModel,
} from '../Match/classifierModelCapabilities';

let cachedModelCatalogue = null;
let catalogueFetchPromise = null;

function fetchModelCatalogue() {
  if (cachedModelCatalogue) return Promise.resolve(cachedModelCatalogue);
  if (catalogueFetchPromise) return catalogueFetchPromise;
  catalogueFetchPromise = axios.get('/ai/models').then((res) => {
    cachedModelCatalogue = res || {models: [], estimatedOutputTokens: 150};
    return cachedModelCatalogue;
  }).catch(() => {
    cachedModelCatalogue = {models: [], estimatedOutputTokens: 150};
    return cachedModelCatalogue;
  });
  return catalogueFetchPromise;
}

function formatModelOptionLabel(entry, catalogue, promptText) {
  const est = estimateClassifierCost({
    catalogue,
    provider: entry.provider,
    model: entry.model,
    resolvedPrompt: promptText || '',
  });
  const ctx = formatContextWindow(entry.contextWindow);
  const perCall = est ? formatCostUsd(est.costUsd) : '—';
  const per1k = est ? formatCostUsd(est.per1000Usd) : '—';
  return `${entry.label} — ${ctx} ctx — ${perCall}/call, ${per1k}/1k`;
}

export const AiBlock = React.memo((node) => {
  const {block, start} = useSelector(createSelectNodeData(node.id), shallowEqual);

  if (!block) {
    return null;
  }
  const {data} = block;

  return (
    <IntNodeComponent name="Ai" icon="ai" data={data} node={node} start={start} />
  );
});

export const AiEditor = ({block}) => {
  const {data} = block;
  const currentMode = data?.outputMode || (data?.outputMappings && data.outputMappings.length > 0 ? 'multiple' : 'single');
  const fileVariableOptions = Chatbot.getFileVariables().filter(
    (opt) => opt.value !== data?.docVariable,
  );

  const [addRowValue, setAddRowValue] = useState('');
  const [localMappings, setLocalMappings] = useState(data?.outputMappings || []);
  const [addRouteValue, setAddRouteValue] = useState('');
  const [localRoutes, setLocalRoutes] = useState(data?.routes || {});
  const [catalogue, setCatalogue] = useState(cachedModelCatalogue || {models: [], estimatedOutputTokens: 150});

  useEffect(() => {
    fetchModelCatalogue().then(setCatalogue);
  }, []);

  useEffect(() => {
    setLocalMappings(data?.outputMappings || []);
  }, [data?.outputMappings]);

  useEffect(() => {
    setLocalRoutes(data?.routes || {});
  }, [data?.routes]);

  const routeVariableOptions = useMemo(() => {
    if (currentMode === 'single') {
      return data?.variable ? [{label: data.variable, value: data.variable}] : [];
    }
    return localMappings.map((m) => ({label: m.variable, value: m.variable}));
  }, [currentMode, data?.variable, localMappings]);

  const catalogueGroups = useMemo(() => groupCatalogueByProvider(catalogue), [catalogue]);

  const selectedProvider = data?.provider || 'openai';
  const selectedModel = data?.model || 'gpt-4o';
  const selectedModelEntry = useMemo(
    () => (catalogue?.models || []).find(
      (entry) => entry.provider === selectedProvider && entry.model === selectedModel,
    ),
    [catalogue, selectedProvider, selectedModel],
  );
  const modelCapabilities = useMemo(
    () => getClassifierModelCapabilities(selectedProvider, selectedModel, selectedModelEntry),
    [selectedProvider, selectedModel, selectedModelEntry],
  );

  if (!block) {
    return null;
  }

  const handleBlur = (e) => {
    Chatbot.setBlockData(block.id, {[e.target.name]: e.target.value});
  };

  const handleModeChange = (mode) => {
    Chatbot.setBlockData(block.id, {assign: {...data, outputMode: mode}});
  };

  const addMapping = (e) => {
    e.preventDefault();
    const value = addRowValue.trim();
    if (value) {
      const newMapping = {
        id: Date.now().toString(),
        variable: value,
        key: value,
      };
      const updatedMappings = [...localMappings, newMapping];
      setLocalMappings(updatedMappings);
      Chatbot.setBlockData(block.id, {assign: {...data, outputMappings: updatedMappings}});
      setAddRowValue('');
    }
  };

  const removeMapping = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedMappings = localMappings.filter((_, i) => i !== index);
    setLocalMappings(updatedMappings);
    Chatbot.setBlockData(block.id, {assign: {...data, outputMappings: updatedMappings}});
  };

  const updateMapping = (index, newValue) => {
    const value = (newValue || '').trim();
    const updatedMappings = localMappings.map((m, i) =>
      i === index ? {...m, variable: value, key: value} : m,
    );
    setLocalMappings(updatedMappings);
    Chatbot.setBlockData(block.id, {assign: {...data, outputMappings: updatedMappings}});
  };

  const selectedRouteVariable = data?.routeVariable
    || (currentMode === 'single' ? data?.variable : routeVariableOptions[0]?.value)
    || '';

  const selectedModelKey = data?.provider && data?.model ? `${data.provider}::${data.model}` : '';
  const supportsThinking = modelCapabilities.supportsThinking === true;
  const supportsEffort = modelCapabilities.supportsEffort === true;
  const effortLevels = modelCapabilities.effortLevels || [];
  const effectiveThinking = data?.thinking ?? modelCapabilities.thinkingDefault ?? 'off';
  const effectiveEffort = data?.effort ?? modelCapabilities.defaultEffort ?? 'high';

  const handleModelChange = (value) => {
    if (!value) {
      const next = {...data, provider: '', model: ''};
      delete next.thinking;
      delete next.effort;
      delete next.cacheControl;
      Chatbot.setBlockData(block.id, {assign: next});
      return;
    }
    const [provider, model] = value.split('::');
    const defaults = defaultAnthropicLlmOptionsForModel(model);
    const next = {...data, provider, model};
    if (defaults) {
      next.thinking = defaults.thinking;
      next.effort = defaults.effort;
    } else {
      delete next.thinking;
      delete next.effort;
    }
    if (provider !== 'anthropic') {
      delete next.cacheControl;
    }
    Chatbot.setBlockData(block.id, {assign: next});
  };

  const handleThinkingChange = (value) => {
    Chatbot.setBlockData(block.id, {assign: {...data, thinking: value}});
  };

  const handleEffortChange = (value) => {
    Chatbot.setBlockData(block.id, {assign: {...data, effort: value}});
  };

  const handleCacheControlChange = (checked) => {
    const next = {...data};
    if (checked) {
      next.cacheControl = true;
    } else {
      delete next.cacheControl;
    }
    Chatbot.setBlockData(block.id, {assign: next});
  };

  const handleRoutingToggle = (enabled) => {
    const patch = {
      ...data,
      routingEnabled: enabled,
    };
    if (enabled && !data?.routeVariable) {
      patch.routeVariable = currentMode === 'single' ? (data?.variable || '') : selectedRouteVariable;
    }
    if (!enabled) {
      patch.routes = {};
      patch.nextOnFallback = '';
    }
    Chatbot.setBlockData(block.id, {assign: patch});
  };

  const handleRouteVariableChange = (value) => {
    Chatbot.setBlockData(block.id, {assign: {...data, routeVariable: value}});
  };

  const addRoute = (e) => {
    e.preventDefault();
    const value = addRouteValue.trim();
    if (!value) return;
    const id = moment().valueOf().toString();
    const updatedRoutes = {
      ...localRoutes,
      [id]: {id, value, next: null},
    };
    setLocalRoutes(updatedRoutes);
    Chatbot.setBlockData(block.id, {assign: {...data, routes: updatedRoutes, routingEnabled: true}});
    setAddRouteValue('');
  };

  const removeRoute = (e, routeId) => {
    e.preventDefault();
    e.stopPropagation();
    Chatbot.removeAiRoute(block.id, routeId);
    const updatedRoutes = {...localRoutes};
    delete updatedRoutes[routeId];
    setLocalRoutes(updatedRoutes);
  };

  const updateRouteValue = (routeId, newValue) => {
    const value = (newValue || '').trim();
    const existing = localRoutes[routeId];
    if (!existing) return;
    const updatedRoutes = {
      ...localRoutes,
      [routeId]: {...existing, value},
    };
    setLocalRoutes(updatedRoutes);
    Chatbot.setBlockData(block.id, {assign: {...data, routes: updatedRoutes}});
  };

  const promptEstimate = data?.aiprompt || '';
  const selectedEntry = selectedModelEntry || (catalogue?.models || []).find((m) => m.model === 'gpt-4o');
  const selectedCost = selectedEntry ? estimateClassifierCost({
    catalogue,
    provider: selectedEntry.provider,
    model: selectedEntry.model,
    resolvedPrompt: promptEstimate,
  }) : null;

  return (
    <>
      <div className="section">AI Input</div>
      <TextareaComponent data={data} name="Prompt" dataname="aiprompt" onBlur={handleBlur} rows="16" />
      <SelectComp
        data={data}
        name="Input file variable (optional)"
        dataname="inputFileVariable"
        placeholder="Select uploaded file"
        options={fileVariableOptions}
        block={block}
        value="label"
      />

      <div className="section" style={{marginTop: '20px'}}>Model</div>
      <div className="mb-2">
        <label htmlFor="ai-model" className="form-label comp-header">AI model</label>
        <select
          id="ai-model"
          className="form-control form-control-sm"
          value={selectedModelKey}
          onChange={(e) => handleModelChange(e.target.value)}
        >
          <option value="">Default (GPT-4o)</option>
          {['openai', 'anthropic'].map((providerKey) => {
            const entries = catalogueGroups[providerKey] || [];
            if (!entries.length) return null;
            return (
              <optgroup key={providerKey} label={providerKey === 'openai' ? 'OpenAI' : 'Anthropic'}>
                {entries.map((entry) => (
                  <option
                    key={`${entry.provider}::${entry.model}`}
                    value={`${entry.provider}::${entry.model}`}
                    disabled={entry.available === false}
                  >
                    {formatModelOptionLabel(entry, catalogue, promptEstimate)}
                    {entry.available === false ? ' (unavailable)' : ''}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        {selectedCost && (
          <p className="small text-muted mb-0 mt-1">
            Est. ~{formatCostUsd(selectedCost.costUsd)} per call ({estimateTokens(promptEstimate)} prompt tokens)
            {catalogue?.pricesReviewedAt ? ` · prices ${catalogue.pricesReviewedAt}` : ''}
          </p>
        )}
        {(catalogueGroups.anthropic || []).some((m) => m.available === false) && (
          <p className="small text-warning mb-0">Anthropic models require ANTHROPIC_API_KEY on the server.</p>
        )}
      </div>

      {supportsThinking || supportsEffort || selectedProvider === 'anthropic' ? (
        <div className="mb-2">
          {supportsThinking || supportsEffort ? (
            <div className="row">
              {supportsThinking && (
                <div className="col-md-4 mb-2">
                  <label htmlFor="ai-thinking" className="form-label comp-header">Thinking</label>
                  <select
                    id="ai-thinking"
                    className="form-control form-control-sm"
                    value={effectiveThinking}
                    onChange={(e) => handleThinkingChange(e.target.value)}
                  >
                    <option value="off">Off</option>
                    <option value="adaptive">Adaptive</option>
                  </select>
                </div>
              )}
              {supportsEffort && (
                <div className="col-md-4 mb-2">
                  <label htmlFor="ai-effort" className="form-label comp-header">Effort</label>
                  <select
                    id="ai-effort"
                    className="form-control form-control-sm"
                    value={effectiveEffort}
                    onChange={(e) => handleEffortChange(e.target.value)}
                  >
                    {effortLevels.map((level) => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedProvider === 'anthropic' && (
                <div className="col-md-4 mb-2 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="ai-cache-control"
                      checked={data?.cacheControl === true}
                      onChange={(e) => handleCacheControlChange(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="ai-cache-control">
                      Cache control
                    </label>
                  </div>
                </div>
              )}
            </div>
          ) : selectedProvider === 'anthropic' ? (
            <div className="row">
              <div className="col-md-4 mb-2 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="ai-cache-control-haiku"
                    checked={data?.cacheControl === true}
                    onChange={(e) => handleCacheControlChange(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="ai-cache-control-haiku">
                    Cache control
                  </label>
                </div>
              </div>
            </div>
          ) : null}
          {data?.cacheControl === true && (
            <p className="small text-muted mb-0">
              Place <code>{'{{CACHE}}'}</code> in the prompt after stable instructions. Keep {'{variables}'} after the marker so they are not cached.
            </p>
          )}
        </div>
      ) : null}

      <div className="section" style={{marginTop: '20px'}}>Execution</div>
      <div className="form-check my-3">
        <input
          type="checkbox"
          id="ai-background-run"
          className="form-check-input"
          checked={!!data?.backgroundRun}
          onChange={(e) => {
            const checked = e.target.checked;
            const next = {...data, backgroundRun: checked};
            if (checked) {
              next.routingEnabled = false;
            }
            if (!checked) {
              delete next.showBackgroundMessage;
            }
            Chatbot.setBlockData(block.id, {assign: next});
          }}
        />
        <label htmlFor="ai-background-run" className="form-check-label">
          Run in background
        </label>
        <div className="form-text">
          User continues immediately; use a Router block later to branch on the saved variable values.
        </div>
      </div>

      {!!data?.backgroundRun && (
        <div className="form-check my-3 ms-4">
          <input
            type="checkbox"
            id="ai-show-background-message"
            className="form-check-input"
            checked={data?.showBackgroundMessage !== false}
            onChange={(e) => {
              Chatbot.setBlockData(block.id, {
                assign: {...data, showBackgroundMessage: e.target.checked},
              });
            }}
          />
          <label htmlFor="ai-show-background-message" className="form-check-label">
            Show run in background message
          </label>
          <div className="form-text">
            When off, the AI still runs in the background but no overlay message is shown.
          </div>
        </div>
      )}

      <div className="section" style={{marginTop: '20px'}}>Output Configuration</div>
      <div className="tab-container">
        <div
          className={`tab btn-section ${currentMode === 'single' ? 'active' : ''}`}
          onClick={() => handleModeChange('single')}
        >
          <span></span>
          Single Variable
        </div>
        <div
          className={`tab btn-section ${currentMode === 'multiple' ? 'active' : ''}`}
          onClick={() => handleModeChange('multiple')}
        >
          <span></span>
          Multiple Variables
        </div>
        <div
          className={`tab btn-section ${currentMode === 'doc' ? 'active' : ''}`}
          onClick={() => handleModeChange('doc')}
        >
          <span></span>
          Google Doc
        </div>
      </div>

      <br />

      {currentMode === 'single' && (
        <div style={{marginBottom: '15px'}}>
          <small style={{fontStyle: 'italic', color: '#666'}}>
            Returns a single text response stored in a single variable
          </small>
        </div>
      )}

      {currentMode === 'multiple' && (
        <div style={{marginBottom: '15px'}}>
          <small style={{fontStyle: 'italic', color: '#666'}}>
            Returns JSON with multiple keys
          </small>
        </div>
      )}

      {currentMode === 'doc' && (
        <div style={{marginBottom: '15px'}}>
          <small style={{fontStyle: 'italic', color: '#666'}}>
            AI returns HTML; server creates a Google Doc and stores links in the doc variable.
          </small>
          <InputComponent data={data} name="Doc variable" dataname="docVariable" onBlur={handleBlur} />
          <InputComponent
            data={data}
            name="Doc file name (supports {variables})"
            dataname="docFileName"
            onBlur={handleBlur}
          />
        </div>
      )}

      <br />

      {currentMode === 'multiple' && (
        <>
          <div className="double-input mb-3">
            <div className="inputs">
              <input
                type="input"
                className="form-control"
                placeholder="Variable name (also JSON key)"
                value={addRowValue}
                onChange={(e) => setAddRowValue(e.target.value)}
                onKeyPress={(e) => (e.key === 'Enter' ? addMapping(e) : null)}
                onBlur={addMapping}
              />
            </div>
            <span className="icon" onClick={addMapping}>
              <i className="fa fa-plus"></i>
            </span>
          </div>

          {localMappings.map((mapping, index) => (
            <div key={mapping.id || index} className="double-input mb-3">
              <div className="inputs">
                <input
                  name={`outputMapping.${mapping.id || index}.name`}
                  className="form-control"
                  placeholder="Variable name (also JSON key)"
                  value={mapping.variable}
                  onChange={(e) => updateMapping(index, e.target.value)}
                />
              </div>
              <span className="icon red" onClick={(e) => removeMapping(e, index)}>
                <i className="fa fa-trash-alt"></i>
              </span>
            </div>
          ))}
        </>
      )}

      {!data?.backgroundRun && (
        <>
      <div className="section" style={{marginTop: '20px'}}>Routing</div>
      <div className="mb-2">
        <label className="form-label comp-header">
          <input
            type="checkbox"
            checked={Boolean(data?.routingEnabled)}
            onChange={(e) => handleRoutingToggle(e.target.checked)}
            style={{marginRight: '8px'}}
          />
          Enable value-based routing
        </label>
        <small style={{display: 'block', fontStyle: 'italic', color: '#666'}}>
          Allowed values are injected into the prompt. Connect each value to a flow branch on the node.
          Unmatched answers use the Fallback exit (left handle).
        </small>
      </div>

      {data?.routingEnabled && (
        <>
          {currentMode === 'multiple' && routeVariableOptions.length > 0 && (
            <div className="mb-2">
              <label htmlFor="route-variable" className="form-label comp-header">Route variable</label>
              <select
                id="route-variable"
                className="form-control form-control-sm"
                value={selectedRouteVariable}
                onChange={(e) => handleRouteVariableChange(e.target.value)}
              >
                {routeVariableOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentMode === 'single' && data?.variable && (
            <p className="small text-muted">Routing variable: <strong>{data.variable}</strong></p>
          )}

          <div className="double-input mb-3">
            <div className="inputs">
              <input
                type="input"
                className="form-control"
                placeholder="Route value (e.g. refund)"
                value={addRouteValue}
                onChange={(e) => setAddRouteValue(e.target.value)}
                onKeyPress={(e) => (e.key === 'Enter' ? addRoute(e) : null)}
                onBlur={addRoute}
              />
            </div>
            <span className="icon" onClick={addRoute}>
              <i className="fa fa-plus"></i>
            </span>
          </div>

          {Object.values(localRoutes).map((route) => (
            <div key={route.id} className="double-input mb-3">
              <div className="inputs">
                <input
                  className="form-control"
                  placeholder="Route value"
                  value={route.value}
                  onChange={(e) => updateRouteValue(route.id, e.target.value)}
                />
              </div>
              <span className="icon red" onClick={(e) => removeRoute(e, route.id)}>
                <i className="fa fa-trash-alt"></i>
              </span>
            </div>
          ))}
        </>
      )}
        </>
      )}
    </>
  );
};
